'use client';
import { imagePathResolver, NumberFormats as nf, ROUTES, useClientConfig, useCurrencyFormatter } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { type ComponentProps, useLayoutEffect, useRef, useState } from 'react';
import { JackpotCarouselContainer } from '..';
import { PromotedCarousel } from '../../../Carousel/index';
import type { GameDetails } from '../../../casino/types/types';
import { GameCard } from '../../../GameCard/GameCardDefault/GameCard';

type GameCopy = GameDetails & { id: number };

function multiplyGames(source: GameDetails[], multiplier: number = 1) {
  const result: GameCopy[] = [];
  for (let copy = 0; copy < multiplier; copy++) {
    for (let i = 0; i < source.length; i++) {
      const sourceGame = source[i];
      if (!sourceGame) continue;
      result.push({ ...sourceGame, id: copy * source.length + i + 1 });
    }
  }
  return result;
}

type CarouselProps = Omit<ComponentProps<typeof JackpotCarouselContainer>, 'children' | 'title'> & {
  namespace: string;
  games: GameDetails[];
  btnMore?: React.ReactNode;
  carouselTitle?: string;
};

/**
 * Displays a looping jackpot carousel. Games are multiplied enough times so that
 * Embla's loop mode has at least 2× the container width of slide content on both sides.
 * The multiplier is recomputed on resize via ResizeObserver.
 * @param namespace - Translation namespace
 * @param games - Game list to display (will be repeated as needed)
 * @param btnMore - Optional "more" button rendered inside the carousel header
 * @param carouselTitle - Optional title forwarded to JackpotCarouselContainer
 */
function Carousel({ namespace, games, btnMore, carouselTitle, ...props }: CarouselProps) {
  const t = useTranslations(namespace);
  const currencyFormatter = useCurrencyFormatter();
  const [promotedSlide, setPromotedSlide] = useState<number>(0);
  const { GAME } = ROUTES.CASINO;
  const { baseImgUrl } = useClientConfig();

  const containerRef = useRef<HTMLDivElement>(null);
  const firstItemRef = useRef<HTMLDivElement | null>(null);

  const [multiplier, setMultiplier] = useState<number>(1);

  const multipliedGames = multiplyGames(games, multiplier);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || !games.length) return;

    const compute = () => {
      const containerWidth = container.clientWidth;

      if (!firstItemRef.current) {
        return;
      }

      const itemWidth = firstItemRef.current.offsetWidth;
      // Embla needs total slide content ≥ 2× container width to create loop clones on both sides.
      setMultiplier(Math.max(1, Math.ceil((containerWidth * 2) / (games.length * itemWidth))));
    };

    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(container);
    return () => ro.disconnect();
  }, [games]);

  return (
    <JackpotCarouselContainer ref={containerRef} {...props} title={carouselTitle}>
      <PromotedCarousel
        title={undefined}
        btnMore={btnMore}
        promotedSlide={promotedSlide}
        setPromotedSlide={setPromotedSlide}
        options={{ prevNextVariant: 'primary' }}
        firstItemRef={firstItemRef}
      >
        {multipliedGames.map(({ imgBkg, logo, name, provider, jackpot, id }, index) => (
          <GameCard
            href={GAME(name)}
            key={id}
            gameName={name}
            backgroundImage={{ background: imagePathResolver(imgBkg ?? '', baseImgUrl, true) }}
            playButtonText={t('play_now')}
            logoImage={imagePathResolver(logo ?? '', baseImgUrl, true)}
            jackpotLabelText={promotedSlide === index && jackpot ? currencyFormatter(jackpot).format(nf.WITHOUT_DECIMALS).get() : undefined}
            id={name}
            gameProvider={provider?.[0] ?? ''}
            position={index}
            backgroundType={promotedSlide === index ? 'image' : 'solid'}
          />
        ))}
      </PromotedCarousel>
    </JackpotCarouselContainer>
  );
}

export { Carousel };
